import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Eye, EyeOff, Lock, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const loginImage =
  "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1400&q=85";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Login failed");
      return;
    }
    toast.success(`Welcome back, ${res.user.name?.split(" ")[0] || ""}`);
    nav(res.user.role === "super_admin" ? "/super-admin" : "/dashboard");
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden overflow-hidden border-r border-black/10 lg:block">
          <img src={loginImage} alt="Outdoor advertising city backdrop" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/70" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.18),rgba(15,23,42,0.92))]" />

          <div className="relative flex h-full flex-col justify-between p-10 xl:p-12">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-950/20">
                <span className="font-display text-lg font-black leading-none">H</span>
              </div>
              <div>
                <div className="font-display text-lg font-black leading-none text-white">Hoardexa</div>
                <div className="mt-1 text-[10px] font-black uppercase tracking-[0.28em] text-white/70">
                  Outdoor Ads
                </div>
              </div>
            </Link>

            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-white">
                <ShieldCheck size={13} /> Secure operating portal
              </div>
              <h1 className="mt-5 font-display text-5xl font-black leading-tight tracking-tight text-white">
                Manage inventory, campaigns, and money from one clean desk.
              </h1>
              <div className="mt-7 grid grid-cols-3 gap-3">
                {[
                  ["Live", "Availability"],
                  ["Smart", "Receivables"],
                  ["Fast", "Proposals"],
                ].map(([label, value]) => (
                  <div key={value} className="rounded-xl border border-white/15 bg-white/10 p-3">
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-200">{label}</div>
                    <div className="mt-1 text-sm font-black text-white">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <main className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full max-w-md"
            data-testid="login-card"
          >
            <div className="mb-8 flex items-center justify-between lg:hidden">
              <Link to="/" className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <span className="font-display text-lg font-black leading-none">H</span>
                </div>
                <div>
                  <div className="font-display text-lg font-black leading-none text-slate-950">Hoardexa</div>
                  <div className="mt-1 text-[10px] font-black uppercase tracking-[0.28em] text-slate-950">
                    Outdoor Ads
                  </div>
                </div>
              </Link>
            </div>

            <div className="rounded-2xl border border-black/15 bg-white p-6 shadow-xl shadow-slate-200/80 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-blue-700">
                    <ShieldCheck size={12} /> Protected
                  </div>
                  <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-slate-950">
                    Sign in
                  </h2>
                  <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600">
                    Enter the owner portal to manage hoardings, campaigns, proposals, and accounts.
                  </p>
                </div>
              </div>

              <form onSubmit={submit} className="mt-7 space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="login-email" className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                    Email Address
                  </label>
                  <div className="flex items-center gap-3 rounded-xl border border-black/15 bg-white px-3.5 py-3 transition focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10">
                    <Mail size={16} className="text-slate-500" />
                    <input
                      data-testid="login-email-input"
                      id="login-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-950 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="login-password" className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-xs font-black text-blue-700 hover:text-blue-600" data-testid="login-forgot-link">
                      Forgot?
                    </Link>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl border border-black/15 bg-white px-3.5 py-3 transition focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10">
                    <Lock size={16} className="text-slate-500" />
                    <input
                      data-testid="login-password-input"
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      autoComplete="current-password"
                      className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-950 outline-none placeholder:text-slate-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-950"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-700/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Signing in..." : <>Sign In <ArrowRight size={15} /></>}
                </button>
              </form>

              <div className="mt-6 grid gap-2 border-t border-slate-200 pt-5">
                {["Role-based access", "Private business data", "Fast owner dashboard"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-xs font-bold text-slate-600">
                    <CheckCircle2 size={15} className="text-emerald-600" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
